import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaPlus, FaTrash, FaFileAlt } from 'react-icons/fa';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import 'pdfjs-dist/build/pdf.worker';
import BaseUrl from '../../../base-api';

function BlockNotifications() {
    const [title, setTitle] = useState({ hindi: '', english: '' });
    const [subtitles, setSubtitles] = useState([{ hindi: '', english: '', files: [] }]);
    const [loading, setLoading] = useState(false);

    const maxSize = 10 * 1024 * 1024; // 10MB
    const minSize = 7 * 1024 * 1024;  // 7MB

    const compressPDF = async (file) => {
        const fileBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: fileBuffer }).promise;

        let bestFile = null;
        let bestSize = Infinity;

        let quality = 1.0;
        let scaleFactor = 1.5;

        for (let attempt = 0; attempt < 7; attempt++) {
            const doc = new jsPDF({ unit: 'mm', format: 'a4' });

            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: scaleFactor });

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: ctx, viewport }).promise;

                const imgData = canvas.toDataURL('image/jpeg', quality);
                if (i > 1) doc.addPage();
                doc.addImage(imgData, 'JPEG', 0, 0, 210, 297);
            }

            const blob = doc.output('blob');
            const compressed = new File([blob], file.name.replace('.pdf', '-compressed.pdf'), {
                type: 'application/pdf',
            });

            const fileSize = compressed.size;
            console.log(`Attempt ${attempt + 1}: ${(fileSize / 1024 / 1024).toFixed(2)} MB`);

            if (fileSize >= minSize && fileSize <= maxSize) {
                return compressed;
            }

            if (fileSize >= minSize && fileSize < bestSize) {
                bestFile = compressed;
                bestSize = fileSize;
            }

            if (fileSize > maxSize) {
                quality = Math.max(0.4, quality - 0.1);
                scaleFactor = Math.max(1.0, scaleFactor - 0.1);
            } else {
                quality = Math.min(1.0, quality + 0.05);
                scaleFactor = Math.min(3.0, scaleFactor + 0.2);
            }
        }

        if (bestFile) {
            alert("⚠️ Couldn't compress perfectly between 7–10MB. Using closest match above 7MB.");
            return bestFile;
        }

        alert("❌ Compression failed to meet target size. Uploading last compressed version.");
        const finalDoc = new jsPDF({ unit: 'mm', format: 'a4' });

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: scaleFactor });

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({ canvasContext: ctx, viewport }).promise;
            const imgData = canvas.toDataURL('image/jpeg', quality);
            if (i > 1) finalDoc.addPage();
            finalDoc.addImage(imgData, 'JPEG', 0, 0, 210, 297);
        }

        const fallbackBlob = finalDoc.output('blob');
        return new File([fallbackBlob], file.name.replace('.pdf', '-compressed.pdf'), {
            type: 'application/pdf',
        });
    };

    const handleSubtitleChange = (index, field, value) => {
        const updated = [...subtitles];
        updated[index][field] = value;
        setSubtitles(updated);
    };

    const handleFileChange = async (index, fileList) => {
        setLoading(true);
        const files = Array.from(fileList);
        const processedFiles = [];

        for (const file of files) {
            if (file.type === 'application/pdf') {
                let compressed = file;
                if (file.size > maxSize) {
                    compressed = await compressPDF(file);
                }

                if (compressed.size <= maxSize) {
                    processedFiles.push(compressed);
                } else {
                    alert(`❌ ${file.name} is still too large even after compression.`);
                }
            } else {
                alert(`❌ ${file.name} is not a PDF.`);
            }
        }

        const updated = [...subtitles];
        updated[index].files = processedFiles;
        setSubtitles(updated);
        setLoading(false);
    };

    const addSubtitle = () => {
        setSubtitles([...subtitles, { hindi: '', english: '', files: [] }]);
    };

    const removeSubtitle = (index) => {
        const updated = subtitles.filter((_, i) => i !== index);
        setSubtitles(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append('title_Hindi', title.hindi);
        formData.append('title', title.english);
        formData.append('location', 'Seminar');

        const subtitleMeta = subtitles.map((s, idx) => ({
            subtitle_Hindi: s.hindi,
            subtitle: s.english,
            index: idx,
        }));
        formData.append('subtitleJson', JSON.stringify(subtitleMeta));

        subtitles.forEach((s, index) => {
            s.files.forEach((file) => {
                formData.append(`files_${index}`, file);
            });
        });

        const token = sessionStorage.getItem('userToken');

        try {
            const res = await axios.post(`${BaseUrl}/api/notificationsBlock/create`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `${token}`,
                },
                timeout: 80000,
            });

            if (res.status === 200 || res.status === 201) {
                alert('✅ Uploaded successfully!');
                setTitle({ hindi: '', english: '' });
                setSubtitles([{ hindi: '', english: '', files: [] }]);
            } else {
                alert('❌ Upload failed.');
            }
        } catch (err) {
            console.error('Error:', err);
            alert('❌ Upload error occurred.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container my-4">
            {/* Loader Overlay */}
            {loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex justify-content-center align-items-center" style={{ zIndex: 1050 }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            <div className="card shadow-sm border-0">
                <div className="card-body px-4 py-3">
                    <h5 className="mb-4 fw-bold">📤 Upload Block Title & Subtitles</h5>

                    <form onSubmit={handleSubmit}>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Main Title (Hindi)</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={title.hindi}
                                    onChange={(e) => setTitle({ ...title, hindi: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Main Title (English)</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={title.english}
                                    onChange={(e) => setTitle({ ...title, english: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        {subtitles.map((sub, index) => (
                            <div key={index} className="border rounded p-3 mb-3 bg-light-subtle">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>📁 Subtitle #{index + 1}</strong>
                                    {subtitles.length > 1 && (
                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeSubtitle(index)}>
                                            <FaTrash className="me-1" /> Remove
                                        </button>
                                    )}
                                </div>

                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Subtitle (Hindi)"
                                            value={sub.hindi}
                                            onChange={(e) => handleSubtitleChange(index, 'hindi', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Subtitle (English)"
                                            value={sub.english}
                                            onChange={(e) => handleSubtitleChange(index, 'english', e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <input
                                    type="file"
                                    className="form-control form-control-sm"
                                    multiple
                                    accept="application/pdf"
                                    onChange={(e) => handleFileChange(index, e.target.files)}
                                />

                                {sub.files.length > 0 && (
                                    <ul className="list-unstyled mt-2 small">
                                        {sub.files.map((file, i) => (
                                            <li key={i}>
                                                <FaFileAlt className="me-1 text-muted" />
                                                {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}

                        <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={addSubtitle} disabled={loading}>
                            <FaPlus className="me-1" /> Add Subtitle
                        </button>

                        <div className="text-end">
                            <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Uploading...
                                    </>
                                ) : (
                                    '✅ Submit Form'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default BlockNotifications;
